/**
 * Sound Effects Hook
 *
 * Provides sound effect playback for AI-generated content.
 * Automatically initializes the sound service on mount.
 */

import { useEffect, useCallback, useRef } from 'react';
import { soundService } from '../services/soundService';

export function useSoundEffects() {
  const initializedRef = useRef(false);

  // Initialize sound service on mount
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      soundService.init().catch((error) => {
        console.error('Failed to initialize sound service:', error);
      });
    }
  }, []);

  // Play a sound by ID
  const playSound = useCallback(async (soundId: string) => {
    await soundService.playSound(soundId);
  }, []);

  // Extract and play sounds from text
  const playSoundsFromText = useCallback(async (text: string) => {
    // Match [SFX:sound-id] tags
    const soundTags = text.match(/\[SFX:([a-z0-9-]+)\]/gi);
    if (!soundTags) return;

    // Play each sound sequentially
    for (const tag of soundTags) {
      const match = tag.match(/\[SFX:([a-z0-9-]+)\]/i);
      if (match) {
        const soundId = match[1];
        await playSound(soundId);
        // Small delay between sounds
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }, [playSound]);

  // Strip sound tags from text
  const stripSoundTags = useCallback((text: string): string => {
    // Remove SFX tags: [SFX:sound-id]
    let cleaned = text.replace(/\[SFX:[a-z0-9-]+\]/gi, '');

    // Remove voice direction tags: [NARRATOR], [COMPUTER], [PA], [NPC:type]
    cleaned = cleaned.replace(/\[NARRATOR\]/gi, '');
    cleaned = cleaned.replace(/\[COMPUTER\]/gi, '');
    cleaned = cleaned.replace(/\[PA\]/gi, '');
    cleaned = cleaned.replace(/\[NPC:[^\]]+\]/gi, '');

    // Remove voice closing tags: [/NARRATOR], [/COMPUTER], [/PA], [/NPC]
    cleaned = cleaned.replace(/\[\/(NARRATOR|COMPUTER|PA|NPC)\]/gi, '');

    // Remove intonation tags: [SARCASM], [PANIC], etc. and their closing tags
    cleaned = cleaned.replace(/\[(SARCASM|PANIC|WHISPER|DRAMATIC|DEADPAN|CHEERFUL|THREATENING|EXHAUSTED|EXCITED|NERVOUS|SMUG)\]/gi, '');
    cleaned = cleaned.replace(/\[\/(SARCASM|PANIC|WHISPER|DRAMATIC|DEADPAN|CHEERFUL|THREATENING|EXHAUSTED|EXCITED|NERVOUS|SMUG)\]/gi, '');

    // Clean up any double spaces or newlines left by tag removal
    cleaned = cleaned.replace(/\n\n\n+/g, '\n\n'); // Multiple newlines to double
    cleaned = cleaned.replace(/  +/g, ' '); // Multiple spaces to single

    return cleaned.trim();
  }, []);

  return {
    playSound,
    playSoundsFromText,
    stripSoundTags,
  };
}
